import { Suspense, lazy } from 'react';
import { Icono } from '@xiltrion/ui/icono';
import { Button } from '@xiltrion/ui/button';
import { Spinner } from '@xiltrion/ui/spinner';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@xiltrion/ui/sheet';
import type { DocumentRow } from '@/api/types';
import { fmtDate } from '@/components/ui';
import { DOC_KIND_LABEL, fmtBytes } from '../lib';

const PdfPages = lazy(() => import('./PdfPages'));

/** Document viewer: preview in a side sheet. */
export function DocumentViewer({ doc, onHide }: { doc: DocumentRow; onHide: () => void }) {
  const url = `/api/v1/documents/${doc.id}/download`;
  const isPdf = doc.mimeType.includes('pdf');
  const isImg = doc.mimeType.startsWith('image/');
  return (
    <Sheet open onOpenChange={(o) => { if (!o) onHide(); }}>
      <SheetContent side="right" className="w-full sm:max-w-[min(900px,96vw)] p-0 gap-0 overflow-hidden">
        <SheetHeader className="border-b pr-12 flex-row items-center gap-3">
          <div className="flex flex-col gap-1.5 min-w-0 flex-1">
            <SheetTitle className="flex items-center gap-2 min-w-0"><Icono nombre='mdi:file-document-outline' className="size-4 shrink-0" aria-hidden /> <span className="truncate">{doc.fileName}</span></SheetTitle>
            <SheetDescription>{DOC_KIND_LABEL[doc.kind] ?? doc.kind} · {fmtBytes(doc.sizeBytes)} · {fmtDate(doc.createdAt)}</SheetDescription>
          </div>
          <Button asChild variant="outline" size="sm"><a href={url} download={doc.fileName}><Icono nombre='mdi:download' /> Descargar</a></Button>
          <Button asChild variant="ghost" size="icon-sm"><a href={url} target="_blank" rel="noreferrer" aria-label="Abrir en pestaña nueva"><Icono nombre='mdi:open-in-new' /></a></Button>
        </SheetHeader>
        <div className="bg-muted flex-1 min-h-0 overflow-auto p-4 flex items-start justify-center">
          {isPdf ? <Suspense fallback={<div className="flex justify-center p-8"><Spinner /></div>}><PdfPages url={url} name={doc.fileName} /></Suspense>
            : isImg ? <img src={url} alt={doc.fileName} className="max-w-full" />
            : <div className="text-muted-foreground text-center p-8"><Icono nombre='mdi:file-document-outline' className="size-10 mx-auto mb-2" aria-hidden />No hay vista previa para <strong>{doc.mimeType}</strong>. Descarga el archivo para abrirlo.</div>}
        </div>
      </SheetContent>
    </Sheet>
  );
}
